'use client';

import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useGetAllCompanyUsersQuery } from 'src/api/redux';
import { Controller } from 'react-hook-form';
import { useAuth } from 'src/context/auth';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { twMerge } from 'tailwind-merge';

interface UserSelectorProps {
  control;
  id: string;
  allUsers?: boolean;
}

const UserSelector = ({ control, id }: UserSelectorProps) => {
  const { user } = useAuth();
  const companyId = user?.company?.id?.toString();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  const { data: companyUsers, isLoading } = useGetAllCompanyUsersQuery(
    { companyId },
    { skip: !companyId }
  );

  const team: { id: string; label: string }[] = useMemo(() => {
    if (!companyUsers) return [];

    return companyUsers
      ?.filter((u) => u.id !== user?.id)
      ?.map(({ id, first_name, last_name }) => ({
        id: String(id),
        label: `${first_name} ${last_name}`,
      }));
  }, [companyUsers, user]);

  const filteredOptions = useMemo(() => {
    if (!searchValue) return team;
    return team.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [team, searchValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideContainer =
        containerRef.current?.contains(target) ?? false;
      const isInsideDropdown =
        dropdownRef.current?.contains(target) ?? false;

      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    const handleResize = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
      }
    },
    [isOpen]
  );

  const listboxId = `${id}-listbox`;

  return (
    <Controller
      name={id}
      control={control}
      render={({ formState: { errors }, field: { onChange, onBlur, value } }) => {
        const selectedOption = team.find((option) => option.id === value);

        return (
          <div
            ref={containerRef}
            className="relative"
            data-testid={`user-selector-${id}`}
            onKeyDown={handleKeyDown}
          >
            <label
              htmlFor={`${id}-input`}
              id={`${id}-label`}
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              {isLoading ? 'Loading...' : 'Select a team member'}
            </label>
            <div
              className={twMerge(
                'flex items-center w-full min-h-10 px-3 py-2 bg-white border border-neutral-200 rounded-full cursor-pointer transition-all',
                isOpen
                  ? 'border-neutral-500 ring-1 ring-neutral-500/20'
                  : 'hover:border-neutral-500'
              )}
              data-testid={`user-selector-${id}-trigger`}
              onClick={() => {
                if (!isOpen) {
                  updatePosition();
                  setTimeout(() => inputRef.current?.focus(), 0);
                }
                setIsOpen(!isOpen);
              }}
            >
              <input
                ref={inputRef}
                id={`${id}-input`}
                type="text"
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-controls={isOpen ? listboxId : undefined}
                aria-labelledby={`${id}-label`}
                aria-autocomplete="list"
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-neutral-500"
                placeholder={selectedOption?.label || 'Search...'}
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  if (!isOpen) {
                    updatePosition();
                    setIsOpen(true);
                  }
                }}
                onBlur={onBlur}
                data-testid={`user-selector-${id}-input`}
              />
              <Icon
                icon={faCaretDown}
                className={twMerge(
                  'w-3.5 h-3.5 text-neutral-500 transition-transform',
                  isOpen && '-rotate-90'
                )}
                aria-hidden="true"
              />
            </div>

            {isOpen &&
              createPortal(
                <div
                  ref={dropdownRef}
                  className="fixed z-[9999]"
                  style={{
                    top: position.top,
                    left: position.left,
                    width: position.width,
                  }}
                >
                  <ul
                    id={listboxId}
                    role="listbox"
                    aria-label="Team members"
                    className="py-1 bg-white border border-neutral-200 rounded-xl shadow-lg max-h-60 overflow-y-auto"
                    data-testid={`user-selector-${id}-listbox`}
                  >
                    {filteredOptions.length === 0 ? (
                      <li
                        className="px-3 py-2 text-sm text-neutral-500"
                        role="option"
                        aria-selected={false}
                        data-testid={`user-selector-${id}-no-results`}
                      >
                        No results found
                      </li>
                    ) : (
                      filteredOptions.map((option) => (
                        <li
                          key={option.id}
                          role="option"
                          aria-selected={selectedOption?.id === option.id}
                          className={twMerge(
                            'px-3 py-2 text-sm cursor-pointer hover:bg-neutral-100',
                            selectedOption?.id === option.id && 'bg-neutral-50'
                          )}
                          onClick={() => {
                            onChange(option.id);
                            setSearchValue('');
                            setIsOpen(false);
                          }}
                          data-testid={`user-selector-${id}-option-${option.id}`}
                        >
                          {option.label}
                        </li>
                      ))
                    )}
                  </ul>
                </div>,
                document.body
              )}

            {errors[id]?.message && (
              <p
                className="mt-1 text-sm text-red-500"
                role="alert"
                data-testid={`user-selector-${id}-error`}
              >
                {errors[id]?.message?.toString()}
              </p>
            )}
          </div>
        );
      }}
    />
  );
};

export default UserSelector;
